import { useRef, useEffect, useLayoutEffect, useState, useMemo, useCallback } from "react"
import { X, Forward, Trash2, Star, Copy } from "lucide-react"
import type { Broadcast, Conversation, Message, Reaction } from "./types"
import { toMessageMedia } from "./attachments"
import { bulkCopyDate, copyTextToClipboard, copyableMessageText, formatBulkCopyText, hasCopyableText } from "./copy-text"
import type { Message as WireMessage } from "./services/messenger.wire"
import { useShallow } from "zustand/react/shallow"
import { othersReadReceiptsHidden, selectUiConversations, useMessengerStore } from "./store/messenger.store"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { useConversationMessages } from "./hooks/useConversationMessages"
import { trace } from "./services/trace"
import { useMarkRead } from "./hooks/useMarkRead"
import { useTyping } from "./hooks/useTyping"
import { useReaction } from "./hooks/useReaction"
import { useEditMessage } from "./hooks/useEditMessage"
import { useDeleteMessage } from "./hooks/useDeleteMessage"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { deleteUploadedShare } from "@/features/meeting/services/share-upload"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import assetNoChats from '@/assets/no_chats.svg'
import { kindForMime as kindFromMime } from "./services/media-probe"
import { getActiveConversationId, setActiveConversationId } from "./store/active-conversation"

/**
 * Adapter — `WireMessage` from the server → existing UI `Message` shape.
 *
 * CDT-5a scope: surface real wire data in the thread without rewriting the
 * UI component tree. Some UI-only flags (`isStarred`, `isPinned`) stay
 * client-side until the backend grows starred/pinned columns — local
 * overlay handlers (handleStar / handlePin / etc.) keep mutating local
 * state on top of this projection.
 *
 * `replyTo` resolves against the loaded thread; absent parent → undefined.
 * Sender display name falls back to "You" / "Member" until the wire
 * shape carries it (CDT — easy follow-up once we hydrate participant
 * names per-message).
 */
function formatTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
}

function formatCallDuration(ms: number | undefined): string | undefined {
  if (typeof ms !== "number" || ms <= 0) return undefined
  const total = Math.round(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, "0")}`
}

/**
 * Render a system message's JSON body into a human sentence. The wire body for
 * a `system` message is a JSON string:
 *   { event, actorId, targetIds, title, previousTitle }
 * with event ∈ created|added|removed|left|renamed|photo_changed. Names resolve
 * against the conversation participants; unknown ids fall back to "Someone".
 */
function buildSystemText(
  body: string | null,
  callerId: string | null,
  nameById: Map<string, string>,
): string {
  if (!body) return ""
  let parsed: {
    event?: string
    actorId?: string
    targetIds?: string[]
    title?: string | null
    previousTitle?: string | null
  }
  try {
    parsed = JSON.parse(body) as typeof parsed
  } catch {
    return body
  }
  const { event, actorId, targetIds = [], title, previousTitle } = parsed
  const actor = actorId && actorId === callerId ? "You" : (actorId ? nameById.get(actorId) ?? "Someone" : "Someone")
  const targetName = (id: string): string =>
    id === callerId ? "you" : nameById.get(id) ?? "someone"
  const targets = targetIds.map(targetName)
  const targetList = targets.length > 0
    ? targets.length === 1
      ? targets[0]
      : `${targets.slice(0, -1).join(", ")} and ${targets[targets.length - 1]}`
    : ""

  switch (event) {
    case "created":
      return `${actor} created the group`
    case "added":
      // Someone who arrived on an invite link is both the actor and the only
      // target, because they added themselves. Left alone that reads "Anitha
      // added Anitha" — or "You added You" in their own view. The server has no
      // `joined` event kind, and adding one would mean a coordinated deploy
      // across sdk-server, signaling and here, so the wording is corrected at
      // the point of rendering.
      if (targetIds.length === 1 && targetIds[0] === actorId) {
        return `${actor} joined`
      }
      return targetList ? `${actor} added ${targetList}` : `${actor} added members`
    case "removed":
      return targetList ? `${actor} removed ${targetList}` : `${actor} removed members`
    case "left":
      return `${actor} left`
    case "renamed":
      // Older messages predate `previousTitle` and only carry the new name.
      return previousTitle && title
        ? `${actor} changed the group name from "${previousTitle}" to "${title}"`
        : title ? `${actor} renamed the group to "${title}"` : `${actor} renamed the group`
    case "photo_changed":
      return `${actor} changed the group photo`
    default:
      return ""
  }
}

function wireToUiMessage(
  w: WireMessage,
  callerId: string | null,
  threadIndex: Map<string, WireMessage>,
  cursors: { otherDeliveredAt: string | null; otherReadAt: string | null },
  pendingIds: Set<string>,
  nameById: Map<string, string>,
): Message {
  // System messages render as a centered pill — no sender / ticks / attachments.
  if (w.type === "system") {
    return {
      id: w.id,
      text: buildSystemText(w.body, callerId, nameById),
      time: formatTime(w.createdAt),
      createdAt: w.createdAt,
      isSentByMe: false,
      status: "sent",
      type: "system",
    }
  }

  const isSentByMe = w.senderId !== null && w.senderId === callerId

  // Tick status (only meaningful for my own messages): compare this message's
  // server timestamp against the other participants' high-water-marks.
  //   sending → optimistic, not yet acked   (clock)
  //   sent    → persisted, not delivered     (✓)
  //   delivered → others' device received it (✓✓ grey)
  //   read    → others opened it             (✓✓ blue)
  let status: Message["status"] = "sent"
  if (isSentByMe) {
    if (pendingIds.has(w.id)) status = "sending"
    else if (cursors.otherReadAt && w.createdAt <= cursors.otherReadAt) status = "read"
    else if (cursors.otherDeliveredAt && w.createdAt <= cursors.otherDeliveredAt) status = "delivered"
  }
  const attachment = w.attachments?.[0]
  let uiType: Message["type"] = "text"
  if (w.type === "call") uiType = "call"
  else if (attachment) {
    // `kind` is authoritative — it's what the sender meant. Mime is only a
    // fallback for rows written before kinds existed.
    switch (attachment.kind ?? kindFromMime(attachment.mime)) {
      case "photo": uiType = "image"; break
      case "video": uiType = "video"; break
      case "audio":
      case "voice": uiType = "audio"; break
      default:      uiType = "file"
    }
  }

  // Host-hosted URL, stored verbatim at send time. Nothing to resolve.
  const attachmentUrl = attachment?.url

  let replyTo: Message["replyTo"]
  if (w.replyTo) {
    const parent = threadIndex.get(w.replyTo)
    if (parent) {
      replyTo = {
        id: parent.id,
        text: parent.body ?? "",
        senderName: parent.senderId === callerId ? "You" : "Member",
      }
    }
  }

  const callPayload = (w.callPayload ?? {}) as Record<string, unknown>
  // A deleted message says who deleted it.
  //
  // The server hands back one lowercase sentence for every reader, so the
  // person who deleted it was told "this message was deleted" about their own
  // message — phrased as though it had happened to them. Which side you are on
  // is known here, and it is the only thing that decides the wording.
  const deletedText = isSentByMe ? "You deleted this message" : "This message was deleted"
  if (w.deletedAt) {
    // Nothing survives a retraction but the sentence saying it happened. The
    // type used to be chosen from the attachment's kind regardless, so a
    // deleted photo went on rendering as a photo with the placeholder tucked
    // underneath it — the thing that was withdrawn still on screen.
    return {
      id: w.id,
      text: deletedText,
      time: formatTime(w.createdAt),
      createdAt: w.createdAt,
      isSentByMe,
      status,
      type: "text",
      isDeleted: true,
    }
  }
  return {
    id: w.id,
    text: w.deletedAt ? deletedText : (w.body ?? ""),
    time: formatTime(w.createdAt),
    createdAt: w.createdAt,
    isSentByMe,
    status,
    type: uiType,
    attachmentUrl,
    attachmentName: attachment?.filename ?? undefined,
    attachmentSize: attachment?.sizeBytes ?? undefined,
    attachmentThumbnailUrl: attachment?.thumbnailUrl ?? undefined,
    attachmentMime: attachment?.mime,
    attachmentWidth: attachment?.width,
    attachmentHeight: attachment?.height,
    attachments: toMessageMedia(w.attachments, kindFromMime),
    audioDuration:
      attachment?.durationMs != null ? Math.round(attachment.durationMs / 1000) : undefined,
    isCall: w.type === "call",
    isMissedCall: callPayload.missed === true,
    callDir: (callPayload.direction as "in" | "out" | undefined) ?? undefined,
    callDuration: formatCallDuration(callPayload.durationMs as number | undefined),
    replyTo,
    senderId: w.senderId ?? undefined,
    reactions: w.reactions?.map((r) => ({ emoji: r.emoji, count: r.count, byMe: r.byMe })),
    // A retracted message is not "edited" — the delete branch above returns
    // before this, so only a live message can carry the marker.
    editedAt: w.editedAt ?? null,
  }
}
import chatBg from "@/assets/chat_bg.png"
import { ConversationHeader } from "./conversation-header"
import { MessageBubble } from "./message-bubble"
import { MessageInput } from "./message-input"
import { useCallsStore } from "./store/calls.store"
import { ProfileInfoPanel } from "./profile-info-panel"
import { BroadcastInfoPanel } from "./broadcast-info-panel"
import type { BroadcastEdit } from "./edit-broadcast-page"
import { BroadcastMessageInfo } from "./broadcast-message-info"
import { useBroadcastReceipts } from "./hooks/useBroadcastReceipts"
import { PinnedMessagesPanel } from "./pinned-messages-panel"
import { StarredMessagesPage } from "./starred-messages-page"
import { ForwardMessageDialog } from "./forward-message-dialog"
import { ImagePreviewOverlay, type PreviewImageItem } from "./image-preview-overlay"
import { DocumentPreviewOverlay } from "./document-preview-overlay"
import { Drawer, DrawerContent } from "@/clientWeb/components/ui/drawer"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/clientWeb/components/ui/dialog"
import { Checkbox } from "@/clientWeb/components/ui/checkbox"
import { Button } from "@/clientWeb/components/ui/button"

interface ConversationPanelProps {
  conversation: Conversation
  initialMessages?: Message[]
  onClose?: () => void
  onForwardMessage?: (messages: Message[], targetIds: string[]) => void
  isMuted?: boolean
  onMuteChatToggle?: () => void
  /** Delete Chat, carrying the dialog's "Keep starred messages" choice. */
  onDeleteConversation?: (opts: { keepStarred: boolean }) => void
  /** Really leave the group, dropping my participant row. Group-only; the
   *  caller decides success/failure once the request resolves, so this
   *  never touches local state itself. */
  onExitGroup?: () => void
  /**
   * Clear Chat. Separate from the local `setMessages([])` below because
   * emptying the panel's own state is only the visible half — without this the
   * server never hears about it and the thread refills on the next reload.
   */
  onClearConversation?: (opts: { keepStarred: boolean }) => void
  onUpdateConversation?: (updates: Partial<Conversation>) => void
  /** Open (or start) a direct chat with a member, from the group's info panel. */
  onMessageMember?: (member: { id: string; name: string; imageUrl?: string }) => void
  chatBackground?: string
  sentBubbleColor?: string
  /** Set when this thread is a BROADCAST LIST (`conversation.isBroadcast`):
   *  sends fan out to the list's recipients and the info drawer shows the list
   *  (recipients / rename / delete) instead of a contact profile. */
  broadcast?: Broadcast
  onEditBroadcast?: (data: BroadcastEdit) => void
  onDeleteBroadcast?: () => void
  onRemoveBroadcastRecipient?: (userId: string) => void
  onBroadcastSent?: () => void
  /** contactPresenceId of the other party when I have BLOCKED them — swaps the
   *  composer for an unblock prompt. Undefined when not blocked. */
  blockedPeerId?: string
  onUnblockPeer?: (peerId: string) => void
  /** Block the other party of this DM (header menu → "Block contact"). */
  onBlockPeer?: () => void
  /** Message ids the caller has starred — drives the star on each bubble.
   *  Server-backed and account-wide, so it's owned by the page, not here. */
  starredIds?: Set<string>
  onToggleStar?: (messageId: string) => void
  /** Unstar a message that's being deleted — one-way, no rollback on failure.
   *  `onToggleStar` would flicker the row back into Starred if the unstar call
   *  failed after the delete had already gone through. */
  onDropStar?: (messageId: string) => void
  /** A group invite link clicked inside a message: open it or ask to join. */
  onOpenInvite?: (conversationId: string) => void
  /** Scroll to this message once the thread is open (jump from Starred). */
  jumpToMessageId?: string | null
  /** Fired once the jump has landed (or been given up on) so the caller
   *  can drop the request and a repeat of the same jump still works. */
  onJumpHandled?: () => void
}

/**
 * The day a message belongs to, and the divider that announces it.
 *
 * This used to be a three-entry map from a FORMATTED TIME STRING to a label —
 * `{"10:10 AM": "Today", "8:00 AM": "Today", "Yesterday 3:00 PM": "Yesterday"}`
 * — left over from mock data. Two consequences, both wrong: a real message that
 * happened to land on 10:10 AM was announced as "Today" however old it was, and
 * every other message got no divider at all, so a thread spanning days showed a
 * column of bare clock faces with nothing to say which day any of them was.
 */
function dayKey(iso: string | undefined): string {
  if (!iso) return ""
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? "" : d.toDateString()
}

function dayLabel(iso: string | undefined): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  if (d.toDateString() === today.toDateString()) return "Today"
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday"
  return d.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" })
}

export function ConversationPanel({ conversation, initialMessages = [], onClose, onForwardMessage, isMuted, onMuteChatToggle, onDeleteConversation, onExitGroup, onClearConversation, onUpdateConversation, onMessageMember, chatBackground, sentBubbleColor, broadcast, onEditBroadcast, onDeleteBroadcast, onRemoveBroadcastRecipient, onBroadcastSent, blockedPeerId, onUnblockPeer, onBlockPeer, starredIds, onToggleStar, onDropStar, onOpenInvite, jumpToMessageId, onJumpHandled }: ConversationPanelProps) {
  const t = useTranslation()
  // CDT-5a — real thread state. `useConversationMessages` REST-seeds + live
  // updates via the channel; the adapter below maps wire → UI shape.
  // Chat identity = contactPresenceId (senderIds + participant.userId are keyed
  // on it now), NOT the auth user id. Null until consented → the thread just
  // renders every message as "not mine" (harmless) until it resolves.
  const myUserId = useAuthStore((s) => s.presenceId) ?? null
  // Broadcast ticks: a list thread has one participant (you), so the cursor
  // path below can only ever say `sent` -- the real receipts are aggregated
  // per message from each recipient's DM copy.
  const isBroadcastThread = !!conversation.isBroadcast
  const [infoMessage, setInfoMessage] = useState<Message | null>(null)
  // Forward-dialog targets. The raw `s.conversations` field is legacy and never
  // populated (hydrate() is unused) — the live conversation list is wire-derived,
  // so use the same selector PresencePage does, else "No contacts found".
  const conversations = useMessengerStore(
    useShallow((s) => selectUiConversations(s, myUserId)),
  )
  const { messages: wireMessages, fetchOlder, hasMoreOlder, isLoading: isLoadingMessages } =
    useConversationMessages(conversation.id)
  const { statusById: broadcastStatusById, refresh: refreshBroadcastReceipts } = useBroadcastReceipts(
    isBroadcastThread ? conversation.id : null,
    wireMessages,
  )
  const { markUpTo } = useMarkRead(conversation.id)
  const { typingUserIds } = useTyping(conversation.id)
  // CDT-5c — real reaction / edit / delete handlers. The hooks do their own
  // optimistic + rollback so the UI feels instant.
  const { toggle: toggleReaction } = useReaction()
  const { edit: editMessageReal, canEdit: canEditWire } = useEditMessage()
  const { remove: deleteMessageReal, canDeleteForEveryone: canDeleteForEveryoneWire } = useDeleteMessage()
  const wireConv = useMessengerStore((s) => s.wireConversations.get(conversation.id))
  const pendingSends = useMessengerStore((s) => s.pendingSends)
  const broadcastCopyIds = useMessengerStore((s) => s.broadcastCopyIds)
  // Set by useMessengerSubscription off the conversation channel: an admin
  // removed me (or I left on another device). The composer below swaps for a
  // notice while this holds; the thread's history stays visible either way.
  const isRemovedFromGroup = useMessengerStore((s) => s.removedFromConversationIds.has(conversation.id))

  // Record which thread is actually on screen: `useIncomingMessageAlerts`
  // (mounted app-wide, outside this component entirely) reads this to skip
  // alerting for the conversation the user is already looking at. This panel
  // is keyed by conversation.id (PresencePage), so switching threads tears
  // one instance down and brings another up in the same commit; the id check
  // on unmount makes sure whichever order those effects run in, the slot is
  // never left cleared by the outgoing instance after the incoming one wrote
  // its own id into it.
  useEffect(() => {
    setActiveConversationId(conversation.id)
    return () => {
      if (getActiveConversationId() === conversation.id) setActiveConversationId(null)
    }
  }, [conversation.id])

  // Ring the DM peer over the SDK. Peer id = the other participant's
  // contactPresenceId; the app-wide GlobalCallLayer renders the call/ring UI.
  const startCall = (mode: "audio" | "video") => {
    /**
     * Only a 1:1 chat has someone to call.
     *
     * Without this the line below took the first participant who is not me and
     * rang them — for a broadcast that is an arbitrary RECIPIENT, who would
     * receive a call they were never the subject of. A broadcast carries
     * `isBroadcast` but not `isGroup`, so a guard on `isGroup` alone (as
     * PresencePage had) does not cover it.
     *
     * Groups are excluded for the same reason: calling "the group" would ring
     * whichever member happened to sort first. PresencePage already refused
     * them; this path did not.
     */
    if (conversation.isBroadcast || conversation.isGroup) return

    const peerId =
      (wireConv?.participants ?? []).find((p) => p.userId !== myUserId)?.userId ??
      (conversation.participants ?? []).find((p) => p.id !== myUserId)?.id
    if (!peerId) return
    useCallsStore.getState().startCall(
      { id: peerId, name: conversation.name, imageUrl: conversation.imageUrl },
      mode,
      { conversationId: conversation.id },
    )
  }
  // Pinned message ids.
  //
  // Held apart from `messages` for the same reason stars are: the list below is
  // rebuilt from the wire whenever anything it depends on changes, and a flag
  // living only on those objects does not survive the rebuild. Starring a
  // message changed `starredIds`, which re-ran the projection, which re-created
  // every message from the wire — and the pin, which existed nowhere else, was
  // gone. Pin and star are independent actions and neither should clear the
  // other.
  //
  // Local rather than server-backed, matching what pinning is today. That is
  // why it resets with the conversation.
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set())

  /** Message the thread has just jumped to, flashed briefly so it can be found. */
  const [highlightedMsgId, setHighlightedMsgId] = useState<string | null>(null)

  const adaptedMessages = useMemo<Message[]>(() => {
    const index = new Map<string, WireMessage>()
    for (const w of wireMessages) index.set(w.id, w)
    // Other participants' delivered/read high-water-marks — min across others so
    // a group message is DELIVERED/READ only once *everyone* else has reached it
    // (for a DM there's exactly one other, so it's just their cursor).
    const others = (wireConv?.participants ?? []).filter((p) => p.userId !== myUserId)
    let otherDeliveredAt: string | null = null
    let otherReadAt: string | null = null
    let dAll = others.length > 0
    let rAll = others.length > 0
    for (const p of others) {
      if (!p.lastDeliveredAt) dAll = false
      else if (otherDeliveredAt === null || p.lastDeliveredAt < otherDeliveredAt) otherDeliveredAt = p.lastDeliveredAt
      if (!p.lastReadAt) rAll = false
      else if (otherReadAt === null || p.lastReadAt < otherReadAt) otherReadAt = p.lastReadAt
    }
    const cursors = {
      otherDeliveredAt: dAll ? otherDeliveredAt : null,
      // Reciprocity: with my own read receipts off I don't see anyone else's
      // blue ticks, so drop their read cursor and let the message fall back to
      // delivered. (Their cursor is also redacted server-side when THEY have
      // receipts off — this is the mirror case, driven by MY setting.)
      otherReadAt: rAll && !othersReadReceiptsHidden() ? otherReadAt : null,
    }
    const pendingIds = new Set(pendingSends.keys())
    // Resolve actor/target ids → display names for system messages.
    const nameById = new Map<string, string>()
    for (const p of wireConv?.participants ?? []) {
      if (p.name) nameById.set(p.userId, p.name)
    }
    const ui = wireMessages.map((w) => {
      const m = wireToUiMessage(w, myUserId, index, cursors, pendingIds, nameById)
      // Stars live on the account, not in this component's state, so they're
      // projected in here rather than toggled locally.
      m.isStarred = starredIds?.has(w.id) ?? false
      // Same reasoning as the star above — projected in, not carried on the
      // object, so a rebuild cannot drop it.
      m.isPinned = pinnedIds.has(w.id)
      // Broadcast copies take their tick from the aggregate receipts instead.
      if (isBroadcastThread && m.isSentByMe && m.status !== "sending") {
        m.status = broadcastStatusById.get(w.id) ?? "sent"
      }
      // My copy of a broadcast, here in the DM it was delivered into. History
      // says so on the message; a copy that just arrived live is known only
      // from the send's response.
      if (m.isSentByMe && !m.isDeleted && (w.viaBroadcast === true || broadcastCopyIds.has(w.id))) {
        m.viaBroadcast = true
      }
      return m
    })
    trace('render', `ConversationPanel  conv=${conversation.id.slice(0, 8)}  painting ${ui.length} messages (${pendingIds.size} pending)`)
    return ui
  }, [wireMessages, myUserId, wireConv, pendingSends, conversation.id, isBroadcastThread, broadcastStatusById, broadcastCopyIds, starredIds, pinnedIds])
  // Keep `messages` as local state so the existing star/pin/forward handlers
  // (UI-only flags + mock send while CDT-5b/5c land) still mutate. Re-sync
  // from wire on every wire-data change.
  const [messages, setMessages] = useState<Message[]>(initialMessages)
  const [searchQuery, setSearchQuery] = useState("")
  const [showProfileInfo, setShowProfileInfo] = useState(false)
  const [showPinned, setShowPinned] = useState(false)
  const [showStarred, setShowStarred] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Message | null>(null)
  const [deleteForEveryone, setDeleteForEveryone] = useState(false)
  const [replyTo, setReplyTo] = useState<Message | null>(null)
  const [editing, setEditing] = useState<Message | null>(null)
  // Everything the forward dialog is about to send, in selection order. A
  // single `Message | null` was why a multi-select forward delivered one
  // message: the extra picks had nowhere to live.
  const [forwardMsgs, setForwardMsgs] = useState<Message[]>([])
  const [isSelectMode, setIsSelectMode] = useState(false)
  const [selectedMsgIds, setSelectedMsgIds] = useState<Set<string>>(new Set())
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  // Pre-ticked to match the single-delete dialog: "for everyone" is only ever
  // offered where it is possible, so when it is offered it defaults on.
  const [massDeleteForEveryone, setMassDeleteForEveryone] = useState(false)
  const [imagePreviewIndex, setImagePreviewIndex] = useState<number | null>(null)
  const [pdfPreview, setPdfPreview] = useState<{ id: string; url: string; name: string; isSentByMe?: boolean } | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  /**
   * Is the reader at the live end of the thread?
   *
   * Kept in a ref rather than state: it is read inside effects and changes on
   * every scroll frame, and rendering the panel again for it would cost more
   * than the fact is worth.
   */
  const atBottomRef = useRef(true)
  /**
   * Set while a scroll-triggered `fetchOlder()` is in flight, to the
   * scroller's `scrollHeight` at the moment it was fired.
   *
   * The layout effect below reads it once the older page lands, to push the
   * scroll position down by exactly the height the new messages added: the
   * reader stays on the same message instead of the whole thread jumping
   * under them. Also doubles as the in-flight guard, so scrolling near the
   * top repeatedly while a page is loading fires the fetch once, not once
   * per scroll event.
   */
  const pendingOlderRef = useRef<number | null>(null)
  const inputRef = useRef<import("./message-input").MessageInputRef | null>(null)
  const [dropOverlayOpen, setDropOverlayOpen] = useState(false)
  const dragDepthRef = useRef(0)

  useEffect(() => {
    setReplyTo(null)
    setPinnedIds(new Set())
    setShowPinned(false)
    setShowStarred(false)
    setImagePreviewIndex(null)
    setPdfPreview(null)
    setSearchQuery("")
   
  }, [conversation.id])

  // CDT-5a — re-seed the local `messages` overlay whenever the wire thread
  // changes. The mock send / star / pin / etc. handlers below still mutate
  // local state on top of this; they'll be replaced by real REST writes in
  // CDT-5b/5c, at which point this sync becomes the only source of truth.
  useEffect(() => {
    setMessages(adaptedMessages)
  }, [adaptedMessages])

  /**
   * Open a conversation at its live end, without animating there.
   *
   * Keyed on the conversation, so it runs once per thread. It used to be keyed
   * on `messages` and animate every time — and `messages` changes for reasons
   * that have nothing to do with new mail: loading older history, a reaction
   * arriving, an edit, a star, a pin. Each of those dragged the reader back
   * down mid-scroll, which is what made older messages so hard to read.
   *
   * `auto` rather than `smooth`: on open there is nothing to follow, and
   * animating from the top of a long thread is the scroll the reader sees
   * happening to them.
   */
  /**
   * Has this thread been placed at its live end yet?
   *
   * Per conversation, because "opening" is not a single moment: the panel
   * mounts with an empty list and the messages arrive after. Scrolling on mount
   * therefore scrolls nothing, and the arrival is what has to do the placing.
   */
  const placedRef = useRef(false)
  useEffect(() => {
    placedRef.current = false
    atBottomRef.current = true
  }, [conversation.id])

  /**
   * Follow new messages only when the reader is already at the bottom.
   *
   * Somebody reading back through the thread stays where they are when a
   * message lands. This is the rule chat clients converge on, because the
   * alternative interrupts reading to show something the reader can reach
   * whenever they choose.
   */
  /**
   * Place the thread, then follow it.
   *
   * The first paint of a conversation jumps to the end with `scrollTop`, in a
   * LAYOUT effect — before the browser paints — so the thread simply appears at
   * its latest message. Anything animated here is a scroll the reader watches
   * happen to them, and `scrollIntoView` even with `behavior: "auto"` still
   * paints the top first and then moves.
   *
   * Only once placed does a new message animate, and only while the reader is
   * already at the end.
   */
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el || messages.length === 0) return

    if (!placedRef.current) {
      el.scrollTop = el.scrollHeight
      placedRef.current = true
      atBottomRef.current = true
      return
    }
    // An older page just landed above what was already on screen. Restoring
    // by the height difference keeps the reader's message under them instead
    // of the follow-the-bottom branch below doing nothing (they are nowhere
    // near the bottom) and the browser doing nothing either (new content
    // above the fold does not move an existing scroll position, so the whole
    // page appeared to jump the moment the new rows pushed everything down).
    if (pendingOlderRef.current !== null) {
      const prevHeight = pendingOlderRef.current
      pendingOlderRef.current = null
      el.scrollTop += el.scrollHeight - prevHeight
      return
    }
    if (atBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" })
    }
  }, [messages])

  /**
   * Within a hair of the end counts as the end: a pixel of drift should not
   * stop the thread following a live conversation.
   *
   * Also loads the next page of history once the reader scrolls near the
   * top, so paging through old messages reads as one continuous scroll
   * instead of stalling at the edge for a manual click each time. The button
   * below the older-messages check stays as a fallback for a thread short
   * enough that the scroller never nears its top at all.
   */
  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
    if (el.scrollTop < 200 && hasMoreOlder && !isLoadingMessages && pendingOlderRef.current === null) {
      pendingOlderRef.current = el.scrollHeight
      void fetchOlder().finally(() => {
        // Normally cleared by the layout effect once the new rows are in the
        // DOM; reached here instead when the fetch added nothing (end of
        // history reached mid-flight, or the request failed), so a later
        // scroll near the top can try again rather than staying blocked.
        pendingOlderRef.current = null
      })
    }
  }, [hasMoreOlder, isLoadingMessages, fetchOlder])

  // CDT-5a — mark up to the latest visible message as read whenever the
  // wire tail advances. `useMarkRead` is 1-s debounced + monotonic + future-
  // clamped so this is safe to call on every render that bumps `messages`.
  useEffect(() => {
    if (adaptedMessages.length === 0) return
    const tail = wireMessages[wireMessages.length - 1]
    if (tail?.createdAt) markUpTo(tail.createdAt)
  }, [adaptedMessages.length, wireMessages, markUpTo])

  const hasFilesInDataTransfer = (dt: DataTransfer | null) => {
    if (!dt) return false
    if (dt.types && Array.from(dt.types).includes("Files")) return true
    return dt.files && dt.files.length > 0
  }

  const handleDragEnter = (e: React.DragEvent) => {
    if (!hasFilesInDataTransfer(e.dataTransfer)) return
    dragDepthRef.current += 1
    setDropOverlayOpen(true)
  }

  const handleDragOver = (e: React.DragEvent) => {
    if (!hasFilesInDataTransfer(e.dataTransfer)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = "copy"
    setDropOverlayOpen(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    if (!hasFilesInDataTransfer(e.dataTransfer)) return
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
    if (dragDepthRef.current === 0) setDropOverlayOpen(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    if (!hasFilesInDataTransfer(e.dataTransfer)) return
    e.preventDefault()
    dragDepthRef.current = 0
    setDropOverlayOpen(false)

    const files = Array.from(e.dataTransfer.files || [])
    const img = files.find(f => f.type.startsWith("image/"))
    if (img) { inputRef.current?.openImageCropWithFile(img); return }
    const video = files.find(f => f.type.startsWith("video/"))
    if (video) { handleSendFile(video, "video"); return }
    const audio = files.find(f => f.type.startsWith("audio/"))
    if (audio) { handleSendFile(audio, "audio-file"); return }
    const other = files[0]
    if (other) handleSendFile(other, "file")
  }

  const displayMessages = useMemo(() => {
    if (!searchQuery.trim()) return messages
    const q = searchQuery.toLowerCase()
    return messages.filter(m => m.text?.toLowerCase().includes(q))
  }, [messages, searchQuery])

  /**
   * The thread cut into consecutive same-day runs.
   *
   * A divider emitted inline, just above the first message of a day, is only
   * on screen if you happen to be scrolled to it — and a thread opens pinned
   * to the BOTTOM. Open a chat whose visible messages share a day and the one
   * pill sits far above the viewport: messages, and no date anywhere. Grouping
   * lets each day's pill stick to the top of the scroller for as long as any
   * of that day's messages are in view, so a date is always on screen.
   *
   * Items keep their index in `displayMessages` — the avatar/tail grouping
   * below compares against the flat neighbours, and a day boundary should
   * start a fresh run there anyway.
   */
  const dayGroups = useMemo(() => {
    const groups: Array<{ key: string; label: string; items: Array<{ msg: Message; idx: number }> }> = []
    displayMessages.forEach((msg, idx) => {
      const k = dayKey(msg.createdAt)
      const open = groups[groups.length - 1]
      if (open && open.key === k) open.items.push({ msg, idx })
      else groups.push({ key: k || `undated-${idx}`, label: dayLabel(msg.createdAt), items: [{ msg, idx }] })
    })
    return groups
  }, [displayMessages])

  /**
   * Messages a "clear chat" would actually remove.
   *
   * System entries are excluded — a new group's only content is the "You
   * created the group" notice, and clearing a thread that holds nothing else
   * removes nothing. `useBroadcastReceipts` draws the same line for the same
   * reason: a system notice is not a message anybody sent.
   */
  const clearableCount = useMemo(
    () => messages.filter(m => m.type !== "system").length,
    [messages],
  )

  const pinnedMessages = useMemo(() => messages.filter(m => m.isPinned), [messages])
  const starredMessages = useMemo(() => messages.filter(m => m.isStarred), [messages])
  /**
   * "Delete for everyone" on a bulk delete, offered only when it is possible
   * for every one of the selected messages: own, not already deleted, and
   * still inside the 15-minute window. A mixed selection falls back to
   * hiding the whole batch for you, rather than retracting the ones that
   * qualify and hiding the rest, which would turn one confirm into two
   * different outcomes with no way to tell which message got which.
   */
  const canMassDeleteForEveryone = useMemo(() => {
    if (selectedMsgIds.size === 0) return false
    for (const id of selectedMsgIds) {
      const w = wireMessages.find(x => x.id === id)
      if (!w || !canDeleteForEveryoneWire(w)) return false
    }
    return true
  }, [selectedMsgIds, wireMessages, canDeleteForEveryoneWire])
  /** Same question, for the single-message delete dialog below. */
  const canDeleteTargetForEveryone = useMemo(() => {
    if (!deleteTarget) return false
    const w = wireMessages.find(x => x.id === deleteTarget.id)
    return !!w && canDeleteForEveryoneWire(w)
  }, [deleteTarget, wireMessages, canDeleteForEveryoneWire])
  /**
   * Everything the preview can page through: photos AND video.
   *
   * Video used to be left out, so tapping one played it in place while tapping
   * a photo opened the full-screen viewer — two different answers to the same
   * gesture, for no reason a reader could see.
   */
  const imageMessages = useMemo(() => {
    // One entry per PICTURE, not per message. A message can carry several, and
    // listing it once meant every photo in an album opened the same first one
    // and the viewer had nothing to page to.
    const list: PreviewImageItem[] = []
    for (const m of messages) {
      if (m.type !== "image" && m.type !== "video") continue
      const media =
        m.attachments && m.attachments.length > 0
          ? m.attachments
          : m.attachmentUrl
            ? [{ url: m.attachmentUrl, name: m.attachmentName, kind: undefined }]
            : []
      for (const a of media) {
        if (!a.url) continue
        const kind: "image" | "video" =
          a.kind === "video" || (a.kind == null && m.type === "video") ? "video" : "image"
        list.push({
          // The message id: deleting from the viewer retracts the message,
          // which is what holds the picture.
          id: m.id,
          url: a.url,
          name: a.name || (kind === "video" ? `video_${list.length + 1}.mp4` : `image_${list.length + 1}.png`),
          kind,
          isSentByMe: m.isSentByMe,
          messageId: m.id,
        })
      }
    }
    return list
  }, [messages])

  const handleSend = (payload: { text: string; linkPreview?: Message["linkPreview"]; imageDataUrl?: string }) => {
    const newMsg: Message = {
      id: `m${Date.now()}`,
      text: payload.text,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      isSentByMe: true,
      status: "sending",
      type: payload.imageDataUrl ? "image" : "text",
      attachmentUrl: payload.imageDataUrl ?? undefined,
      linkPreview: payload.linkPreview,
      replyTo: replyTo
        ? { id: replyTo.id, text: replyTo.text, senderName: replyTo.isSentByMe ? "You" : (replyTo.senderName ?? "Unknown") }
        : undefined,
    }
    setMessages(prev => [...prev, newMsg])
    setReplyTo(null)
    setTimeout(() => {
      setMessages(prev => prev.map(m => m.id === newMsg.id ? { ...m, status: "delivered" } : m))
    }, 800)
  }

  const handleSendAudio = (durationSeconds: number) => {
    const newMsg: Message = {
      id: `m${Date.now()}`,
      type: "audio",
      text: "",
      audioDuration: durationSeconds,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      isSentByMe: true,
      status: "sending",
    }
    setMessages(prev => [...prev, newMsg])
    setTimeout(() => {
      setMessages(prev => prev.map(m => m.id === newMsg.id ? { ...m, status: "delivered" } : m))
    }, 800)
  }

  const handleSendFile = (file: File, type: "image" | "video" | "file" | "audio-file") => {
    const url = URL.createObjectURL(file)
    const msgType =
      type === "image" ? "image" as const
      : type === "video" ? "video" as const
      : "file" as const
    const newMsg: Message = {
      id: `m${Date.now()}`,
      text: "",
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      isSentByMe: true,
      status: "sending",
      type: msgType,
      attachmentUrl: url,
      attachmentName: file.name,
      attachmentSize: file.size,
      replyTo: replyTo
        ? { id: replyTo.id, text: replyTo.text, senderName: replyTo.isSentByMe ? "You" : (replyTo.senderName ?? "Unknown") }
        : undefined,
    }
    setMessages(prev => [...prev, newMsg])
    setReplyTo(null)
    setTimeout(() => {
      setMessages(prev => prev.map(m => m.id === newMsg.id ? { ...m, status: "delivered" } : m))
    }, 800)
  }

  const handleClearMessages = (opts: { keepStarred: boolean }) => {
    setMessages([])
    onClearConversation?.(opts)
  }

  // CDT-5c — real reactions via `useReaction.toggle`. The store applies
  // optimistically + the channel echoes back idempotently (caller-aware
  // store contract from W9), so we just fire and forget here.
  const handleReact = (msg: Message, emoji: string) => {
    void toggleReaction(conversation.id, msg.id, emoji)
  }

  // Server-backed: stars are private, account-wide and must survive a remount.
  // This used to flip a local flag that was lost the moment the thread closed.
  // A deleted message has no content left to mark, so starring it is a no-op:
  // the menu no longer offers Star on one, but the starred drawer's own Unstar
  // routes through this handler too, so the guard lives here as well.
  const handleStar = (msg: Message) => {
    // A deleted message cannot be starred, but one starred before it was
    // deleted must still be unstarrable, or it sits in Starred for good.
    if (msg.isDeleted && !msg.isStarred) return
    onToggleStar?.(msg.id)
  }

  const handlePin = (msg: Message) => {
    setPinnedIds(prev => {
      const next = new Set(prev)
      if (next.has(msg.id)) next.delete(msg.id)
      else next.add(msg.id)
      return next
    })
  }

  // CDT-5c — real soft-delete. The hook does the optimistic
  // `markWireMessageDeleted` (sets `deletedAt = now` + swaps body for the
  // placeholder) → REST DELETE → snapshot-restore on failure. Channel echo
  // lands as idempotent no-op.
  /**
   * Ask first: delete has two meanings and only one of them is undoable.
   *
   * Always opens the dialog, on any message including one already deleted:
   * "for me" is valid on every message, so there is nothing here left to
   * refuse. The dialog itself decides whether "for everyone" is even shown.
   */
  const handleDelete = (msg: Message) => {
    const wireMsg = wireMessages.find((w) => w.id === msg.id)
    setDeleteTarget(msg)
    // Offered pre-ticked only where it is still possible: the sender's own
    // message, not already deleted, within the 15-minute window.
    setDeleteForEveryone(!!wireMsg && canDeleteForEveryoneWire(wireMsg))
  }

  const confirmDelete = async () => {
    const msg = deleteTarget
    if (!msg) return
    const forEveryone = deleteForEveryone && canDeleteTargetForEveryone
    setDeleteTarget(null)

    // Read the attachments BEFORE the delete. Retracting strips them from the
    // message, so looking them up afterwards finds nothing and the objects are
    // left in the bucket with nothing pointing at them.
    const files = forEveryone
      ? (wireMessages.find((w) => w.id === msg.id)?.attachments ?? [])
      : []

    const ok = await deleteMessageReal(conversation.id, msg.id, forEveryone ? "everyone" : "me")
    if (!ok) {
      toast.error({ title: "Delete failed" })
      return
    }

    // Retracted for everyone, so the files go too. A hide leaves them alone —
    // the message still exists for everybody else and still needs them.
    for (const a of files) void deleteUploadedShare(a.url, a.id)

    // A deleted message has nothing left to jump to, so a star pointing at it
    // is just a stale "this message was deleted" row waiting to happen. Only
    // after the delete has actually gone through: an unstar ahead of a delete
    // that then fails would leave the message un-starred for no reason.
    //
    // `onDropStar`, not `onToggleStar`: the toggle rolls back on a failed
    // unstar, which would bring the row back into Starred for a message that
    // is already gone.
    if (msg.isStarred) (onDropStar ?? onToggleStar)?.(msg.id)
  }

  // Editing happens in the composer, the same place replying does. It used to
  // open a `window.prompt`, which announces the page's domain ("app.instavc.ai
  // says"), cannot be styled, and gives none of the affordances — emoji,
  // multi-line, Escape to back out — that the rest of the app has.
  //
  // 15-min window + sender-only checks are mirrored on both client
  // (`canEdit`) and server (`EDIT_WINDOW_EXPIRED` / `NOT_SENDER`).
  const handleEdit = (msg: Message) => {
    const wireMsg = wireMessages.find((w) => w.id === msg.id)
    if (!wireMsg) return
    if (!canEditWire(wireMsg)) {
      toast.error({ title: t("presence.cannotEdit"), description: t("presence.editWindowExpired") })
      return
    }
    // Editing and replying both aim the composer at an existing message, so
    // they cannot both be active.
    setReplyTo(null)
    setEditing({ ...msg, text: wireMsg.body ?? "" })
  }

  /** Save the edit. Returns whether the composer should close. */
  const handleSubmitEdit = async (nextBody: string): Promise<boolean> => {
    if (!editing) return true
    const result = await editMessageReal(editing.id, nextBody)
    if (!result) {
      toast.error({ title: t("presence.editFailed") })
      return false
    }
    return true
  }

  /** Open the viewer on this photo or video. */
  const handleImageClick = (msg: Message) => {
    // The bubble hands back the message with the tapped attachment in the
    // singular fields, so the URL is what says WHICH picture. Falling back to
    // the message is for bubbles that pass themselves unchanged.
    const byUrl = msg.attachmentUrl
      ? imageMessages.findIndex((image) => image.url === msg.attachmentUrl)
      : -1
    const idx = byUrl >= 0 ? byUrl : imageMessages.findIndex((image) => image.id === msg.id)
    if (idx >= 0) setImagePreviewIndex(idx)
  }

  /**
   * A group-invite link inside a message, clicked plain (no modifier key).
   * Handed to PresencePage rather than routed: it is the one place that knows
   * how to turn this id into "open" or "ask to join", and a route change could
   * remount it and lose the selection.
   */
  const handleInviteLinkClick = (conversationId: string) => {
    onOpenInvite?.(conversationId)
  }

  const handleFileClick = (msg: Message) => {
    if (msg.type === "file" && msg.attachmentUrl && msg.attachmentName) {
      // The viewer decides what it can render from the filename; this only
      // has to open it. It used to gate on PDF here as well, so widening the
      // viewer alone would have left every other format unable to reach it.
      setPdfPreview({
        id: msg.id,
        url: msg.attachmentUrl,
        name: msg.attachmentName,
        isSentByMe: msg.isSentByMe,
      })
    }
  }

  const handleDeleteCurrentPreviewImage = (imageId: string) => {
    setMessages(prev => prev.filter(m => m.id !== imageId))
    setImagePreviewIndex((prevIndex) => {
      if (prevIndex === null) return prevIndex
      const remaining = imageMessages.filter((imageMessage) => imageMessage.id !== imageId)
      if (remaining.length === 0) return null
      return Math.min(prevIndex, remaining.length - 1)
    })
  }

  const handleDeletePdf = (pdfId: string) => {
    setMessages(prev => prev.filter(m => m.id !== pdfId))
    setPdfPreview(null)
  }

  const handleUnpin = (msgId: string) => {
    setPinnedIds(prev => {
      if (!prev.has(msgId)) return prev
      const next = new Set(prev)
      next.delete(msgId)
      return next
    })
  }

  const handleEnterSelectMode = () => {
    setIsSelectMode(true)
    setSelectedMsgIds(new Set())
  }

  const handleExitSelectMode = () => {
    setIsSelectMode(false)
    setSelectedMsgIds(new Set())
  }

  const handleToggleSelectMsg = (id: string) => {
    // Deleted messages are not selectable; the bubble already ignores the
    // click, this keeps a stray call from adding one anyway.
    if (messages.find(m => m.id === id)?.isDeleted) return
    setSelectedMsgIds(prev => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  const handleSelectFromMenu = (id: string) => {
    setIsSelectMode(true)
    setSelectedMsgIds(new Set([id]))
  }

  const handleMassDelete = () => {
    setMassDeleteForEveryone(canMassDeleteForEveryone)
    setShowDeleteConfirm(true)
  }

  // Real deletes, one per selected message: a local-only filter left the
  // server never hearing about it, so the batch came back on the next
  // refresh. Continues past individual failures rather than stopping the
  // whole batch on the first one; a single toast at the end says whether
  // anything was left behind, which is all a bulk action can usefully report.
  const confirmMassDelete = async () => {
    const ids = Array.from(selectedMsgIds)
    const forEveryone = massDeleteForEveryone && canMassDeleteForEveryone
    setShowDeleteConfirm(false)
    let anyFailed = false
    for (const id of ids) {
      const ok = await deleteMessageReal(conversation.id, id, forEveryone ? "everyone" : "me")
      if (!ok) { anyFailed = true; continue }
      // Same reasoning as the single-message delete: a star on something just
      // deleted is a stale row in Starred waiting to happen, so clear it the
      // moment the delete is confirmed rather than leaving the batch to do it
      // one-by-one from the drawer. `onDropStar` so a failed unstar doesn't
      // roll the row back into Starred.
      if (starredIds?.has(id)) (onDropStar ?? onToggleStar)?.(id)
    }
    handleExitSelectMode()
    if (anyFailed) toast.error({ title: t("presence.bulkDeleteFailed") })
  }

  const handleMassStar = () => {
    for (const id of selectedMsgIds) onToggleStar?.(id)
    handleExitSelectMode()
  }

  const handleMassForward = () => {
    // Thread order, not click order — a forwarded batch should read the way it
    // read here. `messages` is already oldest→newest.
    const picks = messages.filter(m => selectedMsgIds.has(m.id))
    if (picks.length > 0) setForwardMsgs(picks)
  }

  // One copy for a single message and for a selected batch: both land on the
  // clipboard through the same helper, and both report the same way.
  const reportCopy = (ok: boolean, count: number) => {
    if (!ok) { toast.error({ title: t("presence.copyFailed") }); return }
    toast.success({ title: count === 1 ? t("presence.messageCopied") : t("presence.messagesCopied", { count }) })
  }

  const handleCopy = async (message: Message) => {
    const ok = await copyTextToClipboard(copyableMessageText(message.text))
    reportCopy(ok, 1)
  }

  // Thread order, same as a forward; messages with nothing to copy (an
  // attachment with no caption, say) are left out rather than copied as a
  // blank line. `[<time>, <date>] <name>: <text>` is the shape WhatsApp's own
  // chat export uses, a format people already recognise when it lands
  // somewhere else.
  const handleMassCopy = async () => {
    const picks = messages.filter(m => selectedMsgIds.has(m.id) && hasCopyableText(m))
    if (picks.length === 0) { handleExitSelectMode(); return }
    const text = formatBulkCopyText(picks.map(m => ({
      time: m.time,
      date: bulkCopyDate(m.createdAt, m.time),
      senderName: m.isSentByMe ? "You" : (m.senderName ?? conversation.name ?? "Unknown"),
      text: copyableMessageText(m.text),
    })))
    const ok = await copyTextToClipboard(text)
    handleExitSelectMode()
    reportCopy(ok, picks.length)
  }

  // Disabled rather than hidden: same as the other bulk buttons would be if
  // nothing were selected, so the bar doesn't reshuffle depending on what got
  // picked.
  const canCopySelection = messages.some(m => selectedMsgIds.has(m.id) && hasCopyableText(m))

  const handleJumpTo = (msgId: string) => {
    // Both overlays close first. The starred drawer sits over the thread, so
    // scrolling underneath it while it stayed open looked exactly like the
    // arrow doing nothing — the message was reached and nobody could see it.
    setShowPinned(false)
    setShowStarred(false)
    setTimeout(() => {
      const el = document.getElementById(`msg-${msgId}`)
      if (!el) return
      el.scrollIntoView({ behavior: "smooth", block: "center" })
      // Scrolling alone lands the thread near the message and leaves the reader
      // to work out which one it was — in a busy thread that is most of the
      // work. A brief flash names it. Driven from here rather than through
      // state so it cannot outlive the jump or fight a re-render.
      setHighlightedMsgId(msgId)
      window.setTimeout(() => setHighlightedMsgId((cur) => (cur === msgId ? null : cur)), 1600)
    }, 100)
  }

  // Jump requested from OUTSIDE the panel (tapping a row in Starred opens the
  // thread, then scrolls). Waits for the message to exist in the DOM: the thread
  // is still loading when this mounts, so scrolling immediately would find
  // nothing. Gives up rather than retrying forever if the message isn't in the
  // loaded page — older history would need a paged fetch to reach.
  useEffect(() => {
    if (!jumpToMessageId) return
    let tries = 0
    const timer = setInterval(() => {
      const el = document.getElementById(`msg-${jumpToMessageId}`)
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
        // Same flash the in-thread reply jump uses. Landing the scroll without
        // it drops the reader next to the message and leaves them to work out
        // which one they were sent to.
        setHighlightedMsgId(jumpToMessageId)
        window.setTimeout(
          () => setHighlightedMsgId((cur) => (cur === jumpToMessageId ? null : cur)),
          1600,
        )
        clearInterval(timer)
        onJumpHandled?.()
      } else if (++tries > 20) {
        clearInterval(timer)
        // Clear on giving up too. The request is state held by the parent and
        // nothing used to reset it, so it stayed set at the id of the last
        // jump: asking for the SAME message again changed nothing, the effect
        // never re-ran, and Jump to Message did nothing at all from the second
        // press onwards. It also meant every later page of history re-scrolled
        // the thread back to a message the reader had long since left.
        onJumpHandled?.()
      }
    }, 150)
    return () => clearInterval(timer)
  }, [jumpToMessageId, adaptedMessages.length, onJumpHandled])


  // CDT-5a — real typing indicator from `useTyping`. The store auto-decays
  // typing entries after 5 s when no fresh events arrive (W4), so we just
  // render `typingUserIds.length > 0`. The local user is already filtered
  // at the subscription layer in `useMessengerSubscription`.
  const showTyping = typingUserIds.length > 0

  return (
    <div
      className="flex flex-col h-full min-h-0 min-w-0 overflow-hidden"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isSelectMode ? (
        <div className="bg-card border-b border-border px-4 py-3 flex items-center gap-3 shrink-0 shadow-sm">
          <button
            onClick={handleExitSelectMode}
            className="p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <span className="flex-1 text-sm font-semibold text-foreground">
            {selectedMsgIds.size} selected
          </span>
        </div>
      ) : (
        <ConversationHeader
          conversation={conversation}
          onClose={onClose}
          onOpenInfo={() => setShowProfileInfo(true)}
          onStartAudioCall={() => startCall("audio")}
          onStartVideoCall={() => startCall("video")}
          pinnedCount={pinnedMessages.length}
          messageCount={clearableCount}
          onOpenPinned={() => setShowPinned(true)}
          onOpenStarred={() => setShowStarred(true)}
          onSearchChange={setSearchQuery}
          isMuted={isMuted}
          onMuteChatToggle={onMuteChatToggle}
          onClearMessages={handleClearMessages}
          // A broadcast's delete has to be the broadcast delete — the same one
          // the list's own info panel calls. `onDeleteConversation` removes the
          // owner's thread and leaves the list itself behind, so a menu item
          // reading "Delete broadcast" would not have deleted the broadcast.
          onDeleteChat={(opts) => {
            if (isBroadcastThread && onDeleteBroadcast) onDeleteBroadcast()
            else onDeleteConversation?.(opts)
          }}
          onExitGroup={onExitGroup}
          // Blocking is NOT deleting: the thread stays with its history and the
          // composer is replaced by the unblock prompt. This previously called
          // `onDeleteConversation`, so "Block contact" silently deleted the chat
          // and never created a block — the peer kept messaging normally.
          onBlockContact={() => { onBlockPeer?.() }}
          isBlocked={!!blockedPeerId}
          onUnblockContact={() => { if (blockedPeerId) onUnblockPeer?.(blockedPeerId) }}
          onEnterSelectMode={handleEnterSelectMode}
        />
      )}

      {/* Main area: messages + optional side panel */}
      <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden">
        {/* Messages with tiled chat background */}
        <div className="relative flex-1 min-w-0 overflow-hidden">
          {dropOverlayOpen && (
            <div className="absolute inset-0 z-50 bg-background/70 backdrop-blur-sm">
              <div className="absolute inset-0 grid place-items-center">
                <div className="px-6 py-5 rounded-3xl border border-border bg-card shadow-xl">
                  <div className="text-base font-semibold text-foreground text-center">Drop here</div>
                  <div className="text-xs text-muted-foreground text-center mt-1">Release to attach</div>
                </div>
              </div>
            </div>
          )}
          {/* Background layer — custom or default tiled */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={chatBackground
              ? chatBackground.startsWith("#") || chatBackground.startsWith("linear-gradient") || chatBackground.startsWith("radial-gradient")
                ? { background: chatBackground }
                : { backgroundImage: `url(${chatBackground})`, backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat" }
              : { backgroundImage: `url(${chatBg})`, backgroundRepeat: "repeat", backgroundSize: "360px auto", backgroundPosition: "center", opacity: 0.4 }
            }
          />
          {/* Contrast overlay */}
          <div
            aria-hidden
            className={`absolute inset-0 pointer-events-none ${chatBackground ? "bg-background/50 dark:bg-background/40" : "bg-background/70 dark:bg-background/60"}`}
          />
          {/* Scrollable messages content on top of background */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            // `overflow-x-hidden` is stated rather than left to default. CSS
            // resolves `overflow-x: visible` to `auto` the moment the other
            // axis is not visible, so `overflow-y-auto` alone silently made
            // this horizontally scrollable — and anything inside a bubble that
            // overshot its column gave the whole history a sideways scroll
            // into empty space. Chat scrolls one way only.
            //
            // `min-w-0` so the column can be narrower than its widest child
            // instead of being pushed out by it.
            className="relative h-full min-w-0 overflow-y-auto overflow-x-hidden px-4 py-4 flex flex-col gap-0.5"
          >
          {hasMoreOlder && (
            <div className="flex justify-center mb-2">
              <button
                disabled={isLoadingMessages}
                onClick={() => void fetchOlder()}
                className="text-xs text-muted-foreground bg-muted hover:bg-muted/70 disabled:opacity-50 px-3 py-1 rounded-full"
              >
                {isLoadingMessages ? "Loading…" : "Load older messages"}
              </button>
            </div>
          )}
          {searchQuery.trim() && (
            <div className="flex justify-center mb-2">
              <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                {displayMessages.length} result{displayMessages.length !== 1 ? "s" : ""} for &ldquo;{searchQuery}&rdquo;
              </span>
            </div>
          )}
          {displayMessages.length === 0 && !searchQuery.trim() ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 py-16 select-none">
              <img src={assetNoChats} style={{width:'130px', height:'130px'}} alt=""/>
              <p className="text-sm text-muted-foreground">No messages yet</p>
            </div>
          ) : dayGroups.map(group => (
            <div key={group.key} className="flex flex-col gap-0.5">
              {group.label && (
                <div className="sticky top-0 z-10 flex justify-center py-1 pointer-events-none">
                  <span className="text-xs text-muted-foreground bg-muted/95 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm">
                    {group.label}
                  </span>
                </div>
              )}
              {group.items.map(({ msg, idx }) => {
            const prevMsg = displayMessages[idx - 1]
            const nextMsg = displayMessages[idx + 1]
            const isFirst = !prevMsg || prevMsg.isSentByMe !== msg.isSentByMe || prevMsg.senderName !== msg.senderName
            const isLast  = !nextMsg || nextMsg.isSentByMe !== msg.isSentByMe || nextMsg.senderName !== msg.senderName
            const showAvatar = !msg.isSentByMe && isFirst

            return (
              <div
                key={msg.id}
                id={`msg-${msg.id}`}
                // The flash carries its own backing. A 10% primary tint and a 30%
                // ring were drawn straight onto the chat background, so over a
                // photo or a gradient — anything but the plain default — the
                // message the reply pointed at was not visibly marked at all.
                className={
                  highlightedMsgId === msg.id
                    ? "rounded-lg bg-background/85 ring-2 ring-primary shadow-lg transition-all duration-500"
                    : "rounded-lg ring-0 ring-transparent transition-all duration-500"
                }
              >
                <MessageBubble
                  message={msg}
                  showAvatar={showAvatar}
                  isGroup={conversation.isGroup}
                  onInfo={isBroadcastThread && msg.isSentByMe ? setInfoMessage : undefined}
                  isFirst={isFirst}
                  isLast={isLast}
                  onReply={setReplyTo}
                  onForward={(m: Message) => setForwardMsgs([m])}
                  onCopy={handleCopy}
                  onStar={handleStar}
                  onPin={handlePin}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  onReact={handleReact}
                  onImageClick={handleImageClick}
                  onFileClick={handleFileClick}
                  onInviteLinkClick={handleInviteLinkClick}
                  sentBubbleColor={sentBubbleColor}
                  isSelectMode={isSelectMode}
                  isSelected={selectedMsgIds.has(msg.id)}
                  onSelect={handleToggleSelectMsg}
                  onSelectFromMenu={handleSelectFromMenu}
                  onJumpToMessage={handleJumpTo}
                />
              </div>
            )
              })}
            </div>
          ))}
          {/* Typing indicator */}
          {showTyping && (
            <div className="flex items-end gap-2 mb-1">
              <div className="w-7 shrink-0" />
              <div className="bg-card dark:bg-muted border border-border/50 px-3.5 py-2.5 rounded-2xl rounded-bl-sm shadow-sm flex items-center gap-1">
                {[0, 1, 2].map(i => (
                  <span key={i} className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: `${i * 120}ms` }} />
                ))}
              </div>
              {conversation.typingUser && (
                <span className="text-[10px] text-muted-foreground mb-0.5">{conversation.typingUser} is typing…</span>
              )}
            </div>
          )}
          <div ref={bottomRef} />
          </div>
        </div>

        {/* Pinned messages — Drawer */}
        <Drawer open={showPinned} onOpenChange={setShowPinned}>
          <DrawerContent direction="right" width="sm:max-w-[340px]" className="p-0">
            <PinnedMessagesPanel
              messages={pinnedMessages}
              onClose={() => setShowPinned(false)}
              onJumpTo={handleJumpTo}
              onUnpin={handleUnpin}
            />
          </DrawerContent>
        </Drawer>

        {/* Starred messages — Drawer */}
        <Dialog size="xs" open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          {/* Width comes from `size` on the Dialog above. `maxWidthClass` is
              declared on DialogContent and then dropped — it never reaches the
              class list — so a dialog relying on it fell back to the default
              and rendered as a bar across the screen. */}
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete message?</DialogTitle>
              <DialogDescription>
                {canDeleteTargetForEveryone
                  ? "Choose whether to remove it for everyone or only for you."
                  : "This removes it from your copy of the chat. Everyone else keeps theirs."}
              </DialogDescription>
            </DialogHeader>
            {/* Only offered when it is still possible: the sender's own
                message, not already deleted, within the 15-minute window. */}
            {canDeleteTargetForEveryone && (
              <label className="flex items-center gap-2.5 text-sm text-foreground cursor-pointer select-none">
                <Checkbox
                  checked={deleteForEveryone}
                  onCheckedChange={(v: boolean | 'indeterminate') => setDeleteForEveryone(v === true)}
                />
                {t("presence.deleteForEveryone")}
              </label>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button variant="destructive" onClick={() => void confirmDelete()}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Drawer open={showStarred} onOpenChange={setShowStarred}>
          <DrawerContent direction="right" width="sm:max-w-[340px]" className="p-0">
            <StarredMessagesPage
              starredMessages={starredMessages}
              onClose={() => setShowStarred(false)}
              onJumpTo={handleJumpTo}
              onUnstar={(msg) => handleStar(msg)}
            />
          </DrawerContent>
        </Drawer>
      </div>

      {isSelectMode ? (
        <div className="bg-card border-t border-border shrink-0">
          {selectedMsgIds.size === 0 ? (
            <div className="flex items-center justify-center py-4">
              <span className="text-sm text-muted-foreground">Tap messages to select</span>
            </div>
          ) : (
            <div className="flex items-center justify-around py-3 px-6">
              <button
                onClick={handleMassForward}
                className="flex flex-col items-center gap-1 text-foreground hover:text-primary transition-colors"
              >
                <Forward className="w-5 h-5" />
                <span className="text-[11px]">Forward</span>
              </button>
              <button
                onClick={() => void handleMassCopy()}
                disabled={!canCopySelection}
                className="flex flex-col items-center gap-1 text-foreground hover:text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                <Copy className="w-5 h-5" />
                <span className="text-[11px]">{t("presence.copy")}</span>
              </button>
              <button
                onClick={handleMassStar}
                className="flex flex-col items-center gap-1 text-foreground hover:text-primary transition-colors"
              >
                <Star className="w-5 h-5" />
                <span className="text-[11px]">Star</span>
              </button>
              <button
                onClick={handleMassDelete}
                className="flex flex-col items-center gap-1 text-destructive hover:text-destructive/80 transition-colors"
              >
                <Trash2 className="w-5 h-5" />
                <span className="text-[11px]">Delete</span>
              </button>
            </div>
          )}
        </div>
      ) : isRemovedFromGroup ? (
        // Removed (or left elsewhere): same shape as the block notice below:
        // history stays readable, but there is no action to offer back, so
        // this is read-only until someone re-adds me.
        <div className="border-t border-border bg-muted/30 px-4 py-4 text-center">
          <p className="text-sm text-muted-foreground">{t('presence.errorNotMember')}</p>
        </div>
      ) : blockedPeerId ? (
        // Blocked: the history stays readable (blocking isn't deleting) but the
        // composer is replaced. Sending here would be pointless in one direction
        // and misleading in the other, so unblocking is the only action offered.
        <div className="border-t border-border bg-muted/30 px-4 py-4 text-center">
          <p className="text-sm text-muted-foreground">
            You blocked this contact. You won't receive their messages or calls.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => onUnblockPeer?.(blockedPeerId)}
          >
            Unblock
          </Button>
        </div>
      ) : (
        <MessageInput
          ref={inputRef}
          conversationId={conversation.id}
          onSend={handleSend}
          onSendAudio={handleSendAudio}
          onSendFile={handleSendFile}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          editingMessage={editing}
          onCancelEdit={() => setEditing(null)}
          onSubmitEdit={handleSubmitEdit}
          participants={conversation.participants ?? []}
          isGroup={!!conversation.isGroup}
          isBroadcast={!!conversation.isBroadcast}
          onBroadcastSent={() => { onBroadcastSent?.(); void refreshBroadcastReceipts() }}
        />
      )}

      {/* Profile info — Drawer. A broadcast list gets the LIST panel instead of
          a contact profile (recipients, rename, delete). */}
      <Drawer open={showProfileInfo} onOpenChange={setShowProfileInfo}>
        <DrawerContent direction="right" width="w-[420px]" className="p-0">
          {conversation.isBroadcast && broadcast ? (
          <BroadcastInfoPanel
            broadcast={broadcast}
            onClose={() => setShowProfileInfo(false)}
            onSave={(data) => onEditBroadcast?.(data)}
            onDelete={() => { onDeleteBroadcast?.(); setShowProfileInfo(false) }}
            onRemoveRecipient={(userId) => onRemoveBroadcastRecipient?.(userId)}
          />
          ) : (
          <ProfileInfoPanel
            conversation={conversation}
            starredMessages={starredMessages}
            clearableCount={clearableCount}
            onClose={() => setShowProfileInfo(false)}
            // The panel covers the thread, so the jump has to close it — the
            // same reason the starred drawer closes on its own jump.
            onJumpToMessage={(messageId) => { setShowProfileInfo(false); handleJumpTo(messageId) }}
            // `setActiveCall` does not exist in this component and never did,
            // so both buttons threw a ReferenceError and the call simply never
            // happened. `startCall` above is the one that rings a peer — the
            // same helper the conversation header's own call buttons use.
            onStartAudioCall={() => startCall("audio")}
            onStartVideoCall={() => startCall("video")}
            onClearChat={handleClearMessages}
            onDeleteChat={(opts) => { onDeleteConversation?.(opts); setShowProfileInfo(false) }}
            // No local close here: a successful exit clears `selectedConversation`
            // up in PresencePage, which unmounts this whole panel (drawer
            // included). Closing it eagerly would hide the group even if the
            // request goes on to fail.
            onExitGroup={onExitGroup}
            onUpdateProfile={(data) => onUpdateConversation?.({ ...data })}
            // The chat it opens replaces this one, so the panel goes with it.
            onMessageMember={onMessageMember ? (member) => { setShowProfileInfo(false); onMessageMember(member) } : undefined}
            onMakeAdmin={(memberId) => {
              const updated = conversation.participants?.map(p => p.id === memberId ? { ...p, isAdmin: true } : p)
              onUpdateConversation?.({ participants: updated })
            }}
            onRemoveAdmin={(memberId) => {
              const updated = conversation.participants?.map(p => p.id === memberId ? { ...p, isAdmin: false } : p)
              onUpdateConversation?.({ participants: updated })
            }}
            onRemoveMember={(memberId) => {
              const updated = conversation.participants?.filter(p => p.id !== memberId)
              onUpdateConversation?.({ participants: updated })
            }}
          />
          )}
        </DrawerContent>
      </Drawer>

      {/* Broadcast message info — who has it / has read it */}
      <Drawer open={!!infoMessage} onOpenChange={open => { if (!open) setInfoMessage(null) }}>
        <DrawerContent direction="right" width="w-[420px]" className="p-0">
          {infoMessage && (
            <BroadcastMessageInfo
              broadcastId={conversation.id}
              messageId={infoMessage.id}
              messageText={infoMessage.text}
              onClose={() => setInfoMessage(null)}
            />
          )}
        </DrawerContent>
      </Drawer>

      {/* Forward dialog */}
      <ForwardMessageDialog
        open={forwardMsgs.length > 0}
        messages={forwardMsgs}
        contacts={conversations}
        onClose={() => setForwardMsgs([])}
        onForward={(targetIds) => {
          if (forwardMsgs.length > 0) onForwardMessage?.(forwardMsgs, targetIds)
          setForwardMsgs([])
          // Leaving select mode armed after a forward meant the next tap
          // toggled a checkbox instead of opening a message.
          handleExitSelectMode()
        }}
      />

      <ImagePreviewOverlay
        open={imagePreviewIndex !== null}
        images={imageMessages}
        currentIndex={imagePreviewIndex ?? 0}
        onClose={() => setImagePreviewIndex(null)}
        onChangeIndex={setImagePreviewIndex}
        onDeleteCurrent={handleDeleteCurrentPreviewImage}
      />

      <DocumentPreviewOverlay
        open={pdfPreview !== null}
        pdf={pdfPreview}
        onClose={() => setPdfPreview(null)}
        onDeleteCurrent={handleDeletePdf}
      />

      {/* Bulk delete confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={open => { if (!open) setShowDeleteConfirm(false) }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete {selectedMsgIds.size} message{selectedMsgIds.size !== 1 ? "s" : ""}?</DialogTitle>
            <DialogDescription>
              {canMassDeleteForEveryone
                ? "Choose whether to remove them for everyone or only for you."
                : "This removes the selected messages from your copy of the chat. Everyone else keeps theirs."}
            </DialogDescription>
          </DialogHeader>
          {/* Only offered when every selected message is the sender's own,
              still undeleted, and still inside the 15-minute window: a mixed
              selection has no single answer for "for everyone", so it falls
              back to hiding the whole batch. */}
          {canMassDeleteForEveryone && (
            <label className="flex items-center gap-2.5 text-sm text-foreground cursor-pointer select-none">
              <Checkbox
                checked={massDeleteForEveryone}
                onCheckedChange={(v: boolean | 'indeterminate') => setMassDeleteForEveryone(v === true)}
              />
              {t("presence.deleteForEveryone")}
            </label>
          )}
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => void confirmMassDelete()}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
